/**
 * PB PDP gallery zoom
 * - Desktop (hover + fine pointer): cursor-follow magnify on the active slide image
 * - Touch / coarse pointer: zoom button opens a high-res lightbox
 *
 * Mirrors Dawn magnify.js intent without requiring product modal / loading spinner markup.
 */
(function () {
  if (window.__pbPdpGalleryZoomLoaded) return;
  window.__pbPdpGalleryZoomLoaded = true;

  const ZOOM_SCALE = 2.4;
  const galleriesInited = new WeakSet();
  let lightboxEl = null;
  let lightboxImg = null;
  let lastFocus = null;

  function canHoverZoom() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function zoomSrcFor(img) {
    return img.getAttribute('data-zoom-src') || img.currentSrc || img.src;
  }

  function ensureLightbox() {
    if (lightboxEl) return lightboxEl;

    lightboxEl = document.createElement('div');
    lightboxEl.className = 'pb-pdp-gallery-lightbox';
    lightboxEl.setAttribute('role', 'dialog');
    lightboxEl.setAttribute('aria-modal', 'true');
    lightboxEl.setAttribute('aria-label', 'Zoomed product image');
    lightboxEl.hidden = true;
    lightboxEl.innerHTML =
      '<div class="pb-pdp-gallery-lightbox__inner">' +
      '<button type="button" class="pb-pdp-gallery-lightbox__close" aria-label="Close zoomed image">&times;</button>' +
      '<img class="pb-pdp-gallery-lightbox__img" alt="" />' +
      '</div>';

    lightboxImg = lightboxEl.querySelector('.pb-pdp-gallery-lightbox__img');
    const closeBtn = lightboxEl.querySelector('.pb-pdp-gallery-lightbox__close');

    function close() {
      closeLightbox();
    }

    closeBtn.addEventListener('click', close);
    lightboxEl.addEventListener('click', (e) => {
      if (e.target === lightboxEl) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightboxEl && !lightboxEl.hidden) close();
    });

    document.body.appendChild(lightboxEl);
    return lightboxEl;
  }

  function openLightbox(img) {
    ensureLightbox();
    lastFocus = document.activeElement;
    lightboxImg.src = zoomSrcFor(img);
    lightboxImg.alt = img.alt || 'Product image';
    lightboxEl.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    lightboxEl.querySelector('.pb-pdp-gallery-lightbox__close').focus();
  }

  function closeLightbox() {
    if (!lightboxEl || lightboxEl.hidden) return;
    lightboxEl.hidden = true;
    lightboxImg.removeAttribute('src');
    document.documentElement.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
    lastFocus = null;
  }

  function setZoomOrigin(img, event) {
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = x.toFixed(2) + '% ' + y.toFixed(2) + '%';
  }

  function startHoverZoom(img, event) {
    if (prefersReducedMotion()) return;
    img.style.setProperty('--pb-gallery-zoom-scale', String(ZOOM_SCALE));
    setZoomOrigin(img, event);
    img.classList.add('is-zooming');
  }

  function moveHoverZoom(img, event) {
    if (!img.classList.contains('is-zooming')) return;
    setZoomOrigin(img, event);
  }

  function endHoverZoom(img) {
    img.classList.remove('is-zooming');
    img.style.removeProperty('transform-origin');
  }

  /**
   * Dawn-style click-and-hover overlay (fallback when reduced-motion prefers no scale).
   */
  function createMagnifyOverlay(img, event) {
    const existing = img.parentElement.querySelector('.pb-pdp-gallery__magnify');
    if (existing) {
      existing.remove();
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'pb-pdp-gallery__magnify';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.backgroundImage = 'url("' + zoomSrcFor(img) + '")';

    function position(e) {
      const rect = overlay.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      overlay.style.backgroundPosition = x + '% ' + y + '%';
      overlay.style.backgroundSize = rect.width * ZOOM_SCALE + 'px';
    }

    overlay.addEventListener('mousemove', position);
    overlay.addEventListener('mouseleave', () => overlay.remove());
    overlay.addEventListener('click', () => overlay.remove());

    img.parentElement.insertBefore(overlay, img);
    position(event);
  }

  function activeSlideImage(gallery) {
    const slides = gallery.querySelectorAll('[data-pb-gallery-slide]');
    if (!slides.length) return gallery.querySelector('.pb-pdp-gallery__product-img');

    const track = gallery.querySelector('[data-pb-gallery-track]');
    let index = 0;
    if (track) {
      const transform = track.style.transform || '';
      const match = transform.match(/-?([\d.]+)%/);
      if (match) {
        index = Math.round(parseFloat(match[1]) / 100);
      }
    }
    const slide = slides[index] || slides[0];
    return slide ? slide.querySelector('.pb-pdp-gallery__product-img') : null;
  }

  function initGalleryZoom(gallery) {
    if (galleriesInited.has(gallery)) return;
    galleriesInited.add(gallery);

    const images = gallery.querySelectorAll('.pb-pdp-gallery__product-img');
    if (!images.length) return;

    const zoomBtn = gallery.querySelector('[data-pb-gallery-zoom-open]');

    if (canHoverZoom()) {
      images.forEach((img) => {
        img.addEventListener('mouseenter', (e) => {
          if (prefersReducedMotion()) return;
          startHoverZoom(img, e);
        });
        img.addEventListener('mousemove', (e) => moveHoverZoom(img, e));
        img.addEventListener('mouseleave', () => endHoverZoom(img));
        img.addEventListener('click', (e) => {
          // Reduced-motion / secondary path: Dawn-style overlay magnify
          if (prefersReducedMotion()) {
            e.preventDefault();
            createMagnifyOverlay(img, e);
          }
        });
      });
    }

    zoomBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const img = activeSlideImage(gallery);
      if (img) openLightbox(img);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-pb-gallery][data-pb-gallery-zoom]').forEach(initGalleryZoom);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
